/**
 * The documents behind the numbers — Aadhaar and PAN cards, the cancelled cheque, degree
 * certificates, relieving letters, the profile photo.
 *
 * ── Why this does not use Amplify Storage like everything else ────────────────────────────
 *
 * Receipts, nomination photos, PMS attachments and certification documents all upload straight
 * from the browser through `aws-amplify/storage`. That works because every one of those pages is
 * behind a sign-in, so Amplify can trade the session for identity-pool credentials and sign an
 * S3 request itself.
 *
 * The page that collects Aadhaar, PAN and the bank account is the one page in this application
 * that is NOT behind a sign-in. An invited employee opens it from an email and has no account at
 * all, and the identity pool is created with `allow_unauthenticated_identities = false`
 * (terraform/modules/cognito/main.tf), so there is nothing for Amplify to sign with. Turning
 * guest identities on to fix that would hand every anonymous visitor the authenticated role's S3
 * grant, which reaches the whole bucket.
 *
 * So the browser never talks to AWS. It asks the employee-profile Lambda for one presigned URL
 * for one file and PUTs to that. The signed-in pages use the same path rather than a second one:
 * two upload routes to the same objects is two sets of rules to keep in agreement, and the
 * authenticated role can read the whole bucket anyway — so routing reads through the Lambda,
 * which checks the key against the employee it belongs to, is what actually keeps one person's
 * Aadhaar scan away from another's.
 *
 * ── The key is the server's to choose ─────────────────────────────────────────────────────
 *
 * Nothing here builds a key. `uploadEmployeeDocument` sends a name, a type and a size, and the
 * key comes back. That is deliberate: it makes "write onto somebody else's record" a request the
 * client cannot phrase, rather than one the server has to notice.
 */

import { generateClient } from 'aws-amplify/api';
import { canonicalizeEmail } from './identity';

let client: any = null;
const getClient = () => {
  if (!client) client = generateClient();
  return client;
};

/**
 * Which auth mode to use, decided by whether we hold an invitation token.
 *
 * A token means the onboarding page, which has no session — so `apiKey`, exactly as
 * employeeProfileService does for the other three public fields. Everything else is a signed-in
 * page and runs as `userPool`.
 */
const modeFor = (token?: string) => (token ? { authMode: 'apiKey' as const } : { authMode: 'userPool' as const });

const getData = (result: any): any => ('data' in result ? result.data : null);

const errorMessage = (err: any, fallback: string): string =>
  err?.errors?.[0]?.message || err?.message || fallback;

/** Raised when the two document fields are not on the deployed schema yet. */
export const DOCUMENTS_BACKEND_UNAVAILABLE =
  'Document uploads are not available in this environment yet. ' +
  'They need the AppSync schema and the employee profile service to be deployed.';

const isUnknownField = (err: any): boolean => {
  const message = String(err?.errors?.[0]?.message || err?.message || '');
  return /Unknown field|Validation error|Cannot query field|FieldUndefined|Unsupported field/i.test(message);
};

// ─── The rules, mirroring backend/employee-profile/documents.js ───────────────────────────
//
// Two copies, because the Lambda is a separately zipped package with no shared module — the same
// trade-off canonicalizeEmail and normalizePhoneE164 already make. The server's copy is the one
// that decides; this one exists so the employee is told what is wrong while looking at the file
// picker, rather than after a round trip.

/** What each section will take. Keyed the same as `acceptsDocuments` in profileFieldOwnership. */
export const DOCUMENT_SECTION_TYPES: Record<string, string[]> = {
  photo: ['jpg', 'jpeg', 'png'],
  identifications: ['pdf', 'jpg', 'jpeg', 'png'],
  accounts: ['pdf', 'jpg', 'jpeg', 'png'],
  qualifications: ['pdf', 'jpg', 'jpeg', 'png'],
  previousEmployment: ['pdf', 'jpg', 'jpeg', 'png'],
};

export const MAX_DOCUMENT_SIZE_MB = 10;

/**
 * The content type for a file, decided by its EXTENSION rather than by `file.type`.
 *
 * `file.type` is empty for a file picked from some Windows shares and from a few Android
 * pickers, and the Lambda signs the type into the upload URL — so trusting the browser's guess
 * would refuse perfectly ordinary uploads with a message about content types that the employee
 * can do nothing about. The extension is what both sides already agree the file is.
 */
const CONTENT_TYPES: Record<string, string> = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
};

export const extensionOf = (fileName: string): string => {
  const dot = String(fileName || '').lastIndexOf('.');
  return dot > 0 ? fileName.slice(dot + 1).toLowerCase() : '';
};

export const contentTypeFor = (fileName: string): string => CONTENT_TYPES[extensionOf(fileName)] || '';

/** What the employee is told, or null when the file is fine. The server re-checks all of it. */
export function validateDocumentFile(section: string, file: File): string | null {
  const allowed = DOCUMENT_SECTION_TYPES[section];
  if (!allowed) return `Documents cannot be attached to this section.`;

  const extension = extensionOf(file.name);
  if (!extension) return 'That file has no extension, so its type cannot be checked.';
  if (!allowed.includes(extension)) {
    return `Allowed here: ${allowed.join(', ').toUpperCase()}. ".${extension}" is not one of them.`;
  }
  if (file.size <= 0) return 'That file appears to be empty.';
  if (file.size > MAX_DOCUMENT_SIZE_MB * 1024 * 1024) {
    return `That file is ${(file.size / 1024 / 1024).toFixed(1)} MB. The limit is ${MAX_DOCUMENT_SIZE_MB} MB.`;
  }
  return null;
}

/**
 * The name to show for a stored key, with the uuid the server added taken back off.
 *
 * Derived rather than stored, so there is no second copy of the filename that could disagree
 * with the object. Only a full uuid is stripped — `aws-certificate.pdf` keeps its whole name,
 * because silently renaming somebody's file is worse than showing a long one.
 */
const UUID_PREFIX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-/i;

export function documentFileName(key: string): string {
  const last = String(key || '').split('/').pop() || '';
  return UUID_PREFIX.test(last) ? last.slice(37) : last;
}

/** Whether a key names something an <img> can draw, as opposed to a PDF to open in a tab. */
export const isImageDocument = (key: string): boolean =>
  ['jpg', 'jpeg', 'png'].includes(extensionOf(documentFileName(key)));

// ─── The two operations ───────────────────────────────────────────────────────────────────

const createUploadMutation = /* GraphQL */ `
  mutation CreateEmployeeDocumentUpload(
    $token: String
    $employeeEmail: String
    $section: String!
    $fileName: String!
    $contentType: String!
    $contentLength: Int!
  ) {
    createEmployeeDocumentUpload(
      token: $token
      employeeEmail: $employeeEmail
      section: $section
      fileName: $fileName
      contentType: $contentType
      contentLength: $contentLength
    ) {
      key
      uploadUrl
      contentType
      fileName
      expiresIn
    }
  }
`;

const documentUrlQuery = /* GraphQL */ `
  query GetEmployeeDocumentUrl($token: String, $employeeEmail: String, $key: String!) {
    getEmployeeDocumentUrl(token: $token, employeeEmail: $employeeEmail, key: $key)
  }
`;

export interface DocumentScope {
  /** The invitation token, on the onboarding page. Absent on every signed-in page. */
  token?: string;
  /** Whose record. Ignored by the server when a token is present; HR uses it to name anybody. */
  employeeEmail?: string;
}

/**
 * Put one file in S3 and return the key to record against the section.
 *
 * ── Upload first, record second ───────────────────────────────────────────────────────────
 *
 * The key is returned and the caller stores it with the rest of the form. Nothing is written to
 * the profile here, so a file picked and then abandoned leaves an unreferenced object rather
 * than a profile claiming a document the employee did not mean to attach — and a failed upload
 * throws, so a key is never recorded for a file that is not there. That is the same rule
 * uploadReviewAttachmentFile settled for PMS, and for the same reason: a record pointing at
 * nothing looks exactly like a working attachment until somebody clicks it.
 *
 * ── Why the Content-Type is echoed back rather than recomputed ────────────────────────────
 *
 * The server signs it into the URL, so the PUT must carry byte-for-byte what was signed. Sending
 * our own idea of it — even one derived by the same rule — would be a second source of truth for
 * a value S3 compares exactly, and the failure is a 403 that says nothing about content types.
 */
export async function uploadEmployeeDocument(
  scope: DocumentScope,
  section: string,
  file: File,
  onProgress?: (percent: number) => void
): Promise<{ key: string; fileName: string }> {
  const problem = validateDocumentFile(section, file);
  if (problem) throw new Error(problem);

  const contentType = contentTypeFor(file.name);
  if (!contentType) throw new Error(`".${extensionOf(file.name)}" files cannot be uploaded here.`);

  let ticket: any;
  try {
    const result = await getClient().graphql({
      query: createUploadMutation,
      variables: {
        token: scope.token || null,
        employeeEmail: scope.token ? null : canonicalizeEmail(scope.employeeEmail || '') || null,
        section,
        fileName: file.name,
        contentType,
        contentLength: file.size,
      },
      ...modeFor(scope.token),
    });
    ticket = getData(result)?.createEmployeeDocumentUpload;
  } catch (err: any) {
    if (isUnknownField(err)) throw new Error(DOCUMENTS_BACKEND_UNAVAILABLE);
    throw new Error(errorMessage(err, 'That file could not be uploaded.'));
  }

  if (!ticket?.uploadUrl || !ticket?.key) {
    throw new Error('The upload could not be started. Please try again.');
  }

  onProgress?.(0);
  const response = await fetch(ticket.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': ticket.contentType },
    body: file,
  }).catch((err) => {
    /* A network-level rejection here is usually CORS or a dropped connection, neither of which
       the employee can act on differently — so it is reported as one thing rather than as the
       browser's own wording, which says "Failed to fetch" and nothing else. */
    throw new Error(`"${file.name}" could not be sent. Check your connection and try again. (${err?.message || err})`);
  });

  if (!response.ok) {
    throw new Error(`"${file.name}" was refused by storage (${response.status}). Please try again.`);
  }
  onProgress?.(100);

  return { key: ticket.key, fileName: ticket.fileName || documentFileName(ticket.key) };
}

/**
 * A short-lived URL for reading one document.
 *
 * Generated per view and never stored: the URL expires, so one cached on a record would work
 * until it quietly stopped. The same reasoning PMS attachments and certifications both landed on.
 */
export async function getEmployeeDocumentUrl(scope: DocumentScope, key: string): Promise<string> {
  if (!key) throw new Error('That document has no stored reference and cannot be opened.');
  try {
    const result = await getClient().graphql({
      query: documentUrlQuery,
      variables: {
        token: scope.token || null,
        employeeEmail: scope.token ? null : canonicalizeEmail(scope.employeeEmail || '') || null,
        key,
      },
      ...modeFor(scope.token),
    });
    const url = getData(result)?.getEmployeeDocumentUrl;
    if (!url) throw new Error('That document is no longer available.');
    return url;
  } catch (err: any) {
    if (isUnknownField(err)) throw new Error(DOCUMENTS_BACKEND_UNAVAILABLE);
    throw new Error(errorMessage(err, 'That document could not be opened.'));
  }
}
